import { Button, Input, PasswordInput, Text } from "@microbeta/ui";
import { type FC, type MouseEventHandler, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAlternateEmail,
  MdLockOutline,
  MdOutlineAccountCircle,
} from "react-icons/md";
import styles from "./signup-with-email.module.scss";

interface CreateUserRequest {
  email: string;
  password: string;
  fullName: string;
}

interface Props {
  onSubmit: (values: CreateUserRequest) => void;
  isLoading: boolean;
}

export const SignUpForm: FC<Props> = ({ onSubmit, isLoading }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [validationError, setValidationError] = useState("");

  const { t } = useTranslation();

  const validateForm = () => {
    if (!fullName.trim()) {
      setValidationError(t("signUpForm.fullNameRequired"));
      return false;
    }
    if (!email.trim()) {
      setValidationError(t("signUpForm.emailRequired"));
      return false;
    }
    if (!password) {
      setValidationError(t("signUpForm.passwordRequired"));
      return false;
    }
    if (password !== confirmPassword) {
      setValidationError(t("signUpForm.passwordsDoNotMatch"));
      return false;
    }
    if (password.length < 6) {
      setValidationError(t("signUpForm.passwordTooShort"));
      return false;
    }

    setValidationError("");
    return true;
  };

  const handleSubmit: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSubmit({ email: email.trim(), password, fullName: fullName.trim() });
  };

  const isFormValid =
    fullName.trim() &&
    email.trim() &&
    password &&
    confirmPassword &&
    password === confirmPassword;

  return (
    <>
      <Input
        inputContainerClassName={styles.input}
        label={t("signUpForm.fullNameLabel")}
        value={fullName}
        placeholder={t("signUpForm.fullNamePlaceholder")}
        onChange={(e) => setFullName(e.target.value)}
        leftComponent={<MdOutlineAccountCircle />}
        type="text"
        required
      />
      <Input
        inputContainerClassName={styles.input}
        label={t("signUpForm.emailLabel")}
        value={email}
        placeholder={t("signUpForm.emailPlaceholder")}
        onChange={(e) => setEmail(e.target.value)}
        leftComponent={<MdAlternateEmail />}
        type="email"
        required
      />
      <PasswordInput
        inputContainerClassName={styles.input}
        label={t("signUpForm.passwordLabel")}
        value={password}
        placeholder={t("signUpForm.passwordPlaceholder")}
        onChange={(e) => setPassword(e.target.value)}
        leftComponent={<MdLockOutline />}
        required
      />
      <PasswordInput
        inputContainerClassName={styles.input}
        label={t("signUpForm.confirmPasswordLabel")}
        value={confirmPassword}
        placeholder={t("signUpForm.confirmPasswordPlaceholder")}
        onChange={(e) => setConfirmPassword(e.target.value)}
        leftComponent={<MdLockOutline />}
        required
      />

      {validationError && (
        <Text variant="small" className={styles.errorMessage}>
          {validationError}
        </Text>
      )}

      <Button
        variant="primary"
        size="small"
        type="submit"
        className={styles.signUpButton}
        disabled={isLoading || !isFormValid}
        onClick={handleSubmit}
      >
        {isLoading
          ? t("signUpForm.creatingAccount")
          : t("signUpForm.signUpButton")}
      </Button>
    </>
  );
};
